<template>
  <div class="data-table management-main-list-data-table">
    <management-add-button
      :label="$t('AddTicket')"
      @buttonClick="$router.push({ name: 'tickets-create' })"
      :disabled="
        !userHasPermission(
          'third_party_apis_youtrack_issues_store'
        )
      "
      :list-missing-permissions="[]"
    />

    <div class="management-section-wrapper">
      <v-card-title>
        <span class="management-section-title">
          {{ $tc("Ticket", 2) }}
        </span>

        <v-spacer />

        <v-text-field
          :label="$t('Search')"
          :placeholder="$t('Search')"
          :value="searchFromParent"
          @input="updateSearchInParent"
          append-icon="search"
          class="data-table-search"
          single-line
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filterTickets"
        :page.sync="pagination.page"
        :sort-by="pagination.sortBy"
        :sort-desc="pagination.descending"
        :items-per-page.sync="paginationTicketsList.rowsPerPage"
        :search="searchFromParent"
        :loading="loading"
        loading-text=""
        :items-per-page-text="rowsPerPageText"
        :items-per-page-items="rowsPerPageItems"
        :class="{ 'no-show-permission': !userHasPermission('third_party_apis_youtrack_issues_show') }"
        :header-props="{ sortIcon }"
        :footer-props="{ prevIcon, nextIcon, 'items-per-page-options': rowsPerPageItems }"
        @pagination="setRowsOnLocalStorage(dataTableTicketsListId,paginationTicketsList.rowsPerPage)"
        hide-default-header
      >
        <template v-slot:header>
          <thead>
            <tr>
              <table-header-cell
                :header="headers[0]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="!!filters.idReadable"
                :sort-icon="
                  pagination.descending &&
                  headers[0].value === pagination.sortBy
                    ? 'arrow_drop_up'
                    : 'arrow_drop_down'
                "
                style="padding-left: 39px;"
              >
                <v-text-field
                  v-model="filters.idReadable"
                  :label="$tc('ID', 1)"
                  clearable
                  class="text-field"
                />
              </table-header-cell>

              <table-header-cell
                :header="headers[1]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="!!filters.summary"
                :sort-icon="
                  pagination.descending &&
                  headers[1].value === pagination.sortBy
                    ? 'arrow_drop_up'
                    : 'arrow_drop_down'
                "
              >
                <v-text-field
                  v-model="filters.summary"
                  :label="$t('Title')"
                  clearable
                  class="text-field"
                />
              </table-header-cell>

              <table-header-cell
                :header="headers[2]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="!!filters.state"
                :sort-icon="
                  pagination.descending &&
                  headers[2].value === pagination.sortBy
                    ? 'arrow_drop_up'
                    : 'arrow_drop_down'
                "
              >
                <v-autocomplete
                  v-model="filters.state"
                  :label="$t('State')"
                  :items="stateOptions"
                  :pagination="pagination"
                  clearable
                  class="text-field"
                />
              </table-header-cell>

              <table-header-cell
                :header="headers[3]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="!!filters.createdBy"
                :sort-icon="
                  pagination.descending &&
                  headers[3].value === pagination.sortBy
                    ? 'arrow_drop_up'
                    : 'arrow_drop_down'
                "
              >
                <v-autocomplete
                  v-model="filters.createdBy"
                  :label="$t('CreatedBy')"
                  :items="userOptions"
                  :pagination="pagination"
                  clearable
                  class="text-field"
                />
              </table-header-cell>

              <table-header-cell
                :header="headers[4]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="!!filters.created"
              />

              <table-header-cell
                :header="headers[5]"
                :pagination="pagination"
                :sortIsDisabled="true"
              />
            </tr>
          </thead>
        </template>

        <template v-slot:item="props">
          <tr
            @click="viewItem(props.item.id)"
            :class="{ 'cursor-pointer': userHasPermission('third_party_apis_youtrack_issues_show') }"
          >
            <td>{{ props.item.idReadable }}</td>

            <td>{{ props.item.summary }}</td>

            <td>{{ props.item.state }}</td>

            <td>{{ props.item.fibercloud_user_name }}</td>

            <td>{{ props.item.created }}</td>

            <td v-if="userCanEditOrRemoveItems">
              <app-datatable-action-buttons
                :user-can-edit="userHasPermission('third_party_apis_youtrack_issues_update')"
                @edit="editTicket(props.item.id)"
                :user-can-delete="userHasPermission('third_party_apis_youtrack_issues_destroy')"
                @delete="askConfirmationToDeleteItem(props.item)"
              />
            </td>
          </tr>
        </template>

        <template v-slot:pageText="props">
          {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
          {{ props.itemsLength }}
        </template>

        <template v-slot:no-results>
          <data-table-no-results :search="searchFromParent" />
        </template>

        <template v-slot:no-data>
          <div v-if="loading" />
        </template>
      </v-data-table>
    </div>
    <app-dialog-box
      :open="deleteDialog"
      :title="$t('DeleteTicket')"
      :content="$t('DeleteConfirmation') + ' ' + itemToDelete.idReadable + '?'"
      :main-action-label="$t('No')"
      @mainAction="cancelDelete"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="deleteTicket"
      :loading="loadingDelete"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import AppDatatableActionButtons from "@/components/_Molecules/AppDatatableActionButtons";
import DataTableNoResults from "@/components/_Atoms/DataTableNoResults";
import ManagementAddButton from "@/components/Management/ManagementAddButton";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import TableHeaderCell from "@/components/FieldManagement/FieldWork/Data/ActivityListTableHeaderCell";

export default {
  name: "TicketsList",

  components: {
    DataTableNoResults,
    AppDatatableActionButtons,
    ManagementAddButton,
    AppDialogBox,
    TableHeaderCell
  },

  mixins: [managementDatatableMixin, checkPermissionsMixin],

  data() {
    return {
      dataTableTicketsListId: "dataTableTicketsListId" ,
      paginationTicketsList: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableTicketsListId")
      },
      permissionName: "third_party_apis_youtrack_issues",

      tickets: [],
      ticketsFilter: [],

      loadingDelete: false,

      pagination: {
        sortBy: "created",
        descending: true,
        page: 1,
        rowsPerPage: 5
      },

      filters: {
        idReadable: undefined,
        summary: undefined,
        state: undefined,
        createdBy: undefined,
        created: undefined
      }
    };
  },

  computed: {
    mainHeaders() {
      return [
        {
          text: this.$i18n.tc("ID", 1),
          value: "idReadable",
          width: "10%",
          hasFilter: true
        },
        {
          text: this.$i18n.t("Title"),
          value: "summary",
          width: "30%",
          hasFilter: true
        },
        {
          text: this.$i18n.t("State"),
          value: "state",
          width: "20%",
          hasFilter: true
        },
        {
          text: this.$i18n.t("CreatedBy"),
          value: "fibercloud_user_name",
          width: "20%",
          hasFilter: true
        },
        {
          text: this.$i18n.t("CreationDate"),
          value: "created",
          width: "10%"
        },
        {
          text: "",
          value: "",
          width: "10%"
        }
      ];
    },

    filterTickets() {
      let conditions = [];

      if (this.filters.idReadable) {
        conditions.push(this.filterId);
      }

      if (this.filters.summary) {
        conditions.push(this.filterSummary);
      }

      if (this.filters.state) {
        conditions.push(this.filterState);
      }

      if (this.filters.createdBy) {
        conditions.push(this.filterCreatedBy);
      }

      if (this.filters.created) {
        conditions.push(this.filterCreated);
      }

      if (conditions.length > 0) {
        return this.ticketsFilter.filter(ticket => {
          return conditions.every(condition => {
            return condition(ticket);
          });
        });
      }

      return this.ticketsFilter;
    },

    userOptions() {
      let users = [];
      if (this.tickets.length > 0) {
        this.tickets.forEach(ticket => {
          let user = ticket.fibercloud_user_name;
          if (user) {
            users.push({
              value: user,
              text: user
            });
          }
        });
      }
      return users;
    },

    stateOptions() {
      let states = [];
      if (this.tickets.length > 0) {
        this.tickets.forEach(ticket => {
          let state = ticket.state;
          if (state) {
            states.push({
              value: state,
              text: state
            });
          }
        });
      }
      return states;
    }
  },

  mounted() {
    this.getTickets();
  },

  methods: {
    ...mapActions(["fetchTickets", "deleteTicketByID"]),

    async getTickets() {
      this.loading = true;
      try {
        this.tickets = await this.fetchTickets();
      } catch (e) {
        console.log(e);
      } finally {
        this.ticketsFilter = this.tickets;
        this.loading = false;
      }
    },

    editTicket(item) {
      if (this.userHasPermission("third_party_apis_youtrack_issues_update")) {
        this.$router.push({
          name: "tickets-edit",
          params: { id: item }
        });
      }
    },

    viewItem(item) {
      if (this.userHasPermission("third_party_apis_youtrack_issues_show")) {
        this.$router.push({
          name: "tickets-view",
          params: { id: item }
        });
      }
    },

    async deleteTicket() {
      try {
        this.loadingDelete = true;
        await this.deleteTicketByID(this.itemToDelete.id);
      } catch (error) {
        console.error(error);
      } finally {
        this.deleteDialog = false;
        this.loadingDelete = false;
        this.getTickets();
      }
    },

    filterId(item) {
      return item.idReadable
        .toLowerCase()
        .includes(this.filters.idReadable.toLowerCase());
    },

    filterSummary(item) {
      return item.summary
        .toLowerCase()
        .includes(this.filters.summary.toLowerCase());
    },

    filterState(item) {
      return item.state
        .toLowerCase()
        .includes(this.filters.state.toLowerCase());
    },

    filterCreatedBy(item) {
      return item.fibercloud_user_name === this.filters.createdBy;
    },

    filterCreated(item) {
      return item.created === this.filters.created;
    },

    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    }
  }
};
</script>


// WEBPACK FOOTER //
// src/components/Support/TechnicalSupport/TicketsList.vue